// ─────────────────────────────────────────────
//  Seed Provider Short Codes
//  Admin action: writes shortCode onto matched anesthesiologist user docs.
//  Only called from SetupPage "Import official short codes" button (admin-only).
//
//  Uses matchCodesToUsers to avoid any guessing — ambiguous or unmatched
//  codes are reported to the admin via console.log; they can fix names or
//  assign codes by hand via the existing Short Code input on each user row.
//
//  Phase 4.1 note: SPARE_CODES and UNCODED_PROVIDERS are available from
//  src/domain/providerCodes.js for the PDF short-code matcher.
// ─────────────────────────────────────────────
import { auth } from "./config";
import { getDocs, query, where } from "./sdk";
import { usersCol, updateUser } from "./firestore";
import { OFFICIAL_DOCTOR_CODES, matchCodesToUsers } from "../domain/providerCodes";

/**
 * Apply the official short-code roster to anesthesiologist user docs.
 * Runs as the admin who clicked the button; Firestore rules enforce
 * that only admins can update other users' documents.
 *
 * @param {string} facilityId
 * @returns {Promise<{applied: number, skipped: number, unmatched: number, ambiguous: number}>}
 */
export async function seedProviderCodes(facilityId) {
  if (!auth.currentUser) {
    throw new Error("Not authenticated — must be logged in as an admin.");
  }

  // Load all anesthesiologists in this facility
  const snap = await getDocs(
    query(
      usersCol(),
      where("facilityIds", "array-contains", facilityId),
      where("role", "==", "anesthesiologist")
    )
  );

  const users = snap.docs.map((d) => ({
    uid: d.id,
    ...d.data(),
  }));

  const { matches, unmatched, ambiguous } = matchCodesToUsers(
    OFFICIAL_DOCTOR_CODES,
    users
  );

  let applied = 0;
  let skipped = 0;

  for (const { uid, code, name } of matches) {
    const existing = users.find((u) => u.uid === uid);
    if (existing?.shortCode === code) {
      // Already set to the correct code — no write needed
      skipped++;
      continue;
    }
    // Write only the shortCode field; phone/notes are never stored here
    await updateUser(uid, { shortCode: code });
    applied++;
  }

  if (unmatched.length > 0) {
    console.log(
      "[seedProviderCodes] Unmatched codes (no user doc found — assign manually):",
      unmatched.map((u) => `${u.code}: ${u.name}`)
    );
  }

  if (ambiguous.length > 0) {
    console.log(
      "[seedProviderCodes] Ambiguous matches (2+ users share name tokens — assign manually):",
      ambiguous.map((a) => `${a.code}: ${a.name} → candidates ${a.candidateUids.join(", ")}`)
    );
  }

  return {
    applied,
    skipped,
    unmatched: unmatched.length,
    ambiguous: ambiguous.length,
  };
}
